import { VehicleIllustration } from '../../../components/VehicleIllustration';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Camera, Pencil, Star, Trash2 } from 'lucide-react';
import { useIonActionSheet } from '@ionic/react';
import type { VehiclePhotoKind, VehicleStatus } from '@tcrd/shared';
import { Badge, Button, Card, ErrorState, Notice, SectionTitle, Spinner } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { useToast } from '../../../components/toast';
import { api, errorMessage } from '../../../lib/api';
import { pickImage, toForm } from '../../../lib/media';
import { FUEL_LABEL, PHOTO_KIND_LABEL, STATUS_LABEL, VEHICLE_TYPE_LABEL, type VehicleDto } from './types';

const SLOTS: VehiclePhotoKind[] = ['MAIN', 'FRONT', 'SIDE', 'REAR', 'INTERIOR'];

export function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const [present] = useIonActionSheet();
  const [busy, setBusy] = useState<string | null>(null);
  const [affected, setAffected] = useState<Array<{ code: string; scheduledAt: string }>>([]);
  const q = useQuery({ queryKey: ['vehicle', id], queryFn: () => api<VehicleDto>(`/vehicles/${id}`) });

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['vehicle', id] }), qc.invalidateQueries({ queryKey: ['vehicles'] })]);

  const upload = async (kind: VehiclePhotoKind) => {
    try {
      const blob = await pickImage();
      if (!blob) return;
      setBusy(kind);
      await api(`/vehicles/${id}/photos`, { method: 'POST', form: toForm(blob, { kind }, kind.toLowerCase()) });
      await refresh();
      toast('Foto guardada', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const removePhoto = async (photoId: string) => {
    try {
      await api(`/vehicles/${id}/photos/${photoId}`, { method: 'DELETE' });
      await refresh();
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  const changeStatus = () =>
    present({
      header: 'Estado del vehículo',
      buttons: [
        ...(['ACTIVE', 'MAINTENANCE', 'INACTIVE'] as VehicleStatus[]).map((s) => ({
          text: STATUS_LABEL[s],
          handler: () => {
            void (async () => {
              try {
                const r = await api<VehicleDto & { affectedBookings: Array<{ code: string; scheduledAt: string }> }>(`/vehicles/${id}/status`, { method: 'PATCH', body: { status: s } });
                setAffected(r.affectedBookings);
                await refresh();
              } catch (e) {
                toast(errorMessage(e), 'danger');
              }
            })();
          },
        })),
        { text: 'Cancelar', role: 'cancel' },
      ],
    });

  const makePrimary = async () => {
    try {
      await api(`/vehicles/${id}/primary`, { method: 'POST' });
      await refresh();
      toast('Ahora es tu vehículo principal', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  const v = q.data;
  return (
    <Page
      title={v ? `${v.make} ${v.model}` : 'Vehículo'}
      back="/driver/vehicles"
      onRefresh={() => q.refetch()}
      actions={v && <Link to={`/driver/vehicles/${id}/edit`} aria-label="Editar" className="tap flex items-center justify-center px-2"><Pencil className="size-5" /></Link>}
    >
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {v && (
        <div className="flex flex-col gap-5">
          {affected.length > 0 && (
            <Notice tone="warning">
              Este cambio afecta {affected.length} reserva(s) confirmada(s): {affected.map((a) => a.code).join(', ')}. Asigna otro vehículo o informa al cliente; no se cancelaron automáticamente.
            </Notice>
          )}
          {v.photos.length === 0 && (
            <Notice tone="danger">
              <strong>Sube al menos 1 foto de tu vehículo.</strong> Sin foto no podrás recibir viajes. Mientras tanto los clientes ven una ilustración; la foto real les ayuda a identificarte y da confianza.
            </Notice>
          )}
          <Card className="p-5">
            {v.photos.length === 0 && <VehicleIllustration type={v.type} color={v.color} className="w-full max-w-xs h-auto mx-auto mb-3" title={`${v.make} ${v.model} ${v.color}`} />}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone={v.status === 'ACTIVE' ? 'success' : v.status === 'MAINTENANCE' ? 'warning' : 'neutral'}>{STATUS_LABEL[v.status]}</Badge>
              {v.isPrimary && <Badge tone="dark"><Star className="size-3" aria-hidden /> Principal</Badge>}
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
              {[
                ['Placa', v.plate],
                ['Año', v.year],
                ['Color', v.color],
                ['Versión', v.trim ?? '—'],
                ['Tipo', VEHICLE_TYPE_LABEL[v.type]],
                ['Combustible', FUEL_LABEL[v.fuel]],
                ['Puertas', v.doors],
                ['Pasajeros', v.passengerCapacity],
                ['Maletas', v.luggageCapacity],
                ['A/C', v.airConditioning ? 'Sí' : 'No'],
              ].map(([k, val]) => (
                <div key={String(k)}>
                  <dt className="text-[var(--text-muted)]">{k}</dt>
                  <dd className="font-medium">{val}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-5 flex gap-2 flex-wrap">
              <Button size="sm" variant="secondary" onClick={changeStatus}>Cambiar estado</Button>
              {!v.isPrimary && <Button size="sm" variant="secondary" icon={<Star className="size-4" />} onClick={makePrimary}>Hacer principal</Button>}
            </div>
          </Card>

          <section>
            <SectionTitle title="Fotos" subtitle="Mínimo 1 foto. Los clientes ven estas fotos para identificar el vehículo." />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {SLOTS.map((kind) => {
                const photo = v.photos.find((p) => p.kind === kind);
                return (
                  <div key={kind} className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-dashed border-[var(--border)] bg-[var(--surface)]">
                    {photo ? (
                      <>
                        <img src={photo.url} alt={PHOTO_KIND_LABEL[kind]} className="h-full w-full object-cover" />
                        <button onClick={() => removePhoto(photo.id)} className="tap absolute top-1 right-1 rounded-full bg-black/60 text-white size-9 flex items-center justify-center" aria-label={`Eliminar foto ${PHOTO_KIND_LABEL[kind]}`}>
                          <Trash2 className="size-4" />
                        </button>
                      </>
                    ) : null}
                    <button
                      onClick={() => upload(kind)}
                      disabled={busy === kind}
                      className={`absolute inset-x-0 bottom-0 tap flex items-center justify-center gap-1 py-2 text-xs font-semibold ${photo ? 'bg-black/55 text-white' : 'h-full text-[var(--text-muted)] flex-col'}`}
                    >
                      <Camera className="size-5" aria-hidden />
                      {busy === kind ? 'Subiendo…' : `${photo ? 'Cambiar' : 'Agregar'} ${PHOTO_KIND_LABEL[kind].toLowerCase()}`}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </Page>
  );
}
